import { useAuthContext } from "auth/AuthProvider";
import Dropdown from "components/dropdown";
import { ChevronDown, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "api/axios";
import { setAccessToken } from "auth/tokenStorage";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface WorkspacesDropdownProps {
  redirectPath?: string; // Optional redirect path after switching workspace
  skipRedirect?: boolean; // If true, don't navigate after switching
  showViewAll?: boolean; // If false, hide the "View All" link
}

const WorkspacesDropdown: React.FC<WorkspacesDropdownProps> = ({
  redirectPath = "/dashboard/agents",
  skipRedirect = false,
  showViewAll = true,
}) => {
  const { t } = useTranslation();
  const { user, fetchUser } = useAuthContext();
  const [isOpenDropdown, setIsOpenDropDown] = useState(false);
  const [isSwitching, setIsSwitching] = useState<string | null>(null);
  const navigate = useNavigate();
  if (!user || !user.workspaces) {
    return null;
  }

  const workspaces = user.workspaces;
  const displayedWorkspaces = workspaces.slice(0, 5);
  const handleSwitchWorkspace = async (wsId: string) => {
    setIsSwitching(wsId);
    try {
      const response = await api.post(`/workspaces/switch/${wsId}`);
      const { access_token, expires_in } = response.data;
      setAccessToken(access_token, expires_in);
      await fetchUser();

      if (!skipRedirect) {
        navigate(redirectPath);
      }

      toast.success(t("workspaces.workspaceSwitched"));
    } catch (error) {
      toast.error(t("workspaces.switchFailed"));
    } finally {
      setIsSwitching(null);
      setIsOpenDropDown(false);
    }
  };
  return (
    <Dropdown
      isOpen={isOpenDropdown}
      setIsOpen={setIsOpenDropDown}
      button={
        <div className="flex w-max cursor-pointer items-center gap-2 rounded px-3 py-2 hover:bg-gray-100 dark:hover:bg-navy-700">
          <span className="text-sm font-bold text-navy-700 dark:text-white">
            {user.current_workspace?.name || t("common.selectWorkspace")}
          </span>
          <ChevronDown size={16} className="text-gray-700 dark:text-gray-200" />
        </div>
      }
      children={
        <div className="flex min-w-[180px] flex-col rounded-[12px] bg-white p-2 shadow-xl dark:bg-navy-700">
          {displayedWorkspaces.map((ws) => (
            <button
              key={ws.id}
              className={`flex items-center justify-between rounded px-3 py-2 text-left ${
                ws.id === user.current_workspace?.id
                  ? "bg-gray-100 font-semibold dark:bg-navy-600 dark:text-white"
                  : "hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-navy-600"
              }`}
              onClick={
                ws.id === user.current_workspace?.id || isSwitching
                  ? undefined
                  : () => handleSwitchWorkspace(ws.id)
              }
              disabled={ws.id === user.current_workspace?.id || !!isSwitching}
            >
              <div>
                <span
                  className={`${
                    ws.id === user.current_workspace?.id
                      ? "text-navy-700 dark:text-white"
                      : "text-gray-700 dark:text-gray-200"
                  }`}
                >
                  {ws.name}
                </span>
                <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                  {ws.organization_name}
                </span>
              </div>
              {isSwitching === ws.id && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
            </button>
          ))}
          {showViewAll && (
            <Link
              to={"/dashboard/workspaces"}
              onClick={() => setIsOpenDropDown(false)}
              className="mt-1 self-start px-3 py-1 text-blue-600 hover:underline dark:text-blue-300 dark:hover:text-blue-400"
            >
              {t("common.viewAll")}
            </Link>
          )}
        </div>
      }
      classNames="py-2 top-8 w-max right-2"
    />
  );
};

export default WorkspacesDropdown;
